/**
 * Synthetic data, so the portal can be built and reviewed before the API that
 * will feed it exists.
 *
 * Deliberately not all-green. A dashboard demoed only in its happy state gets
 * designed for the happy state, and the whole point of this page is the moment
 * something has quietly stopped working.
 */

import {
  DEFAULT_NOTIFICATION_SETTINGS,
  normalizeNotificationSettings,
  assessJob,
  humanBytes,
  needsAttention,
  normalizePlanOverride,
  overrideInheritsEverything,
  summaryCadenceLabel,
  type BackupJob,
  type Machine,
  type NotificationSettings,
  type PlanNotificationOverride,
  type PortalOverview,
  type Repository,
  type Snapshot,
  type SummaryCadence,
  webhookUrlProblem,
} from "@bigbear/shared";

import { PortalError, type PortalApi } from "./portal";

const HOUR = 3600;
const DAY = 24 * HOUR;

function buildOverview(nowUtc: number): PortalOverview {
  const repositories: Repository[] = [
    {
      id: "repo-nas",
      // A label is what someone called it; a destination is where it actually
      // is. Demoing them as one string hid the fact that they answer different
      // questions — a label like "Primary NAS" says nothing about which NAS.
      label: "Primary NAS",
      destination: "\\\\nas\\backups\\primary",
      kind: "network",
      formatVersion: 1,
      chunkCount: 1_284_903,
      storedBytes: 812_004_233_216,
      logicalBytes: 1_402_113_982_464,
    },
    {
      id: "repo-b2",
      label: "Offsite archive",
      destination:
        "s3://offsite-archive/media-7c1e4b02 (Backblaze B2, us-west-004)",
      kind: "s3",
      formatVersion: 1,
      chunkCount: 1_190_442,
      storedBytes: 764_223_881_216,
      logicalBytes: 1_402_113_982_464,
    },
  ];

  const machines: Machine[] = [
    { id: "DESKTOP-4F2A", name: "Workstation", lastSeenUtc: nowUtc - 2 * HOUR },
    { id: "LAPTOP-X1", name: "Thinkpad", lastSeenUtc: nowUtc - 9 * DAY },
    { id: "HTPC-LOUNGE", name: "Media box", lastSeenUtc: nowUtc - 40 * HOUR },
  ];

  const jobs: BackupJob[] = [
    {
      id: "photos",
      machineId: "DESKTOP-4F2A",
      name: "Photo library",
      sourcePaths: ["D:/Photos"],
      repositoryId: "repo-nas",
    },
    {
      id: "documents",
      machineId: "DESKTOP-4F2A",
      name: "Documents",
      sourcePaths: ["C:/Users/cam/Documents"],
      repositoryId: "repo-nas",
    },
    {
      // Same job id on a second machine: the case a naive lookup gets wrong.
      id: "photos",
      machineId: "LAPTOP-X1",
      name: "Photo library",
      sourcePaths: ["C:/Users/cam/Pictures"],
      repositoryId: "repo-nas",
    },
    {
      id: "media",
      machineId: "HTPC-LOUNGE",
      name: "Media archive",
      sourcePaths: ["E:/Media"],
      expectedIntervalHours: 24 * 7,
      repositoryId: "repo-b2",
    },
    {
      // No repository: the shape a job has when the machine reporting it runs
      // a desktop from before repository ids were sent. Its page shows the
      // sources and says nothing at all about where they go, which is better
      // than guessing at one.
      id: "projects",
      machineId: "DESKTOP-4F2A",
      name: "Projects",
      sourcePaths: ["C:/dev"],
    },
  ];

  const snapshots: Snapshot[] = [
    // Workstation photos: healthy, a run every day for a week.
    ...Array.from({ length: 7 }, (_, i) =>
      run({
        id: `photos-desktop-${i}`,
        jobId: "photos",
        machineId: "DESKTOP-4F2A",
        finishedUtc: nowUtc - (2 * HOUR + i * DAY),
        files: 128_401 + i * 37,
        bytes: 981_273_645_000,
        newBytes: i === 6 ? 981_273_645_000 : 4_192_304_000 - i * 120_000,
      }),
    ),

    // Documents: healthy and small.
    run({
      id: "documents-1",
      jobId: "documents",
      machineId: "DESKTOP-4F2A",
      finishedUtc: nowUtc - 3 * HOUR,
      files: 8_204,
      bytes: 12_884_901_888,
      newBytes: 94_371_840,
    }),

    // Laptop photos: last succeeded nine days ago. Nothing has failed, which is
    // exactly why this one is easy to miss.
    run({
      id: "photos-laptop-1",
      jobId: "photos",
      machineId: "LAPTOP-X1",
      finishedUtc: nowUtc - 9 * DAY,
      files: 44_190,
      bytes: 220_014_998_016,
      newBytes: 1_073_741_824,
    }),

    // Media box: ran last night and failed. Its previous run was fine.
    run({
      id: "media-2",
      jobId: "media",
      machineId: "HTPC-LOUNGE",
      finishedUtc: nowUtc - 14 * HOUR,
      status: "failed",
      files: 1_204,
      bytes: 48_318_382_080,
      newBytes: 2_147_483_648,
    }),
    run({
      id: "media-1",
      jobId: "media",
      machineId: "HTPC-LOUNGE",
      finishedUtc: nowUtc - 6 * DAY,
      files: 9_881,
      bytes: 421_921_161_216,
      newBytes: 8_589_934_592,
    }),
  ];

  return { repositories, machines, jobs, snapshots };
}

function run(
  fields: Pick<
    Snapshot,
    | "id"
    | "jobId"
    | "machineId"
    | "finishedUtc"
    | "files"
    | "bytes"
    | "newBytes"
  > &
    Partial<Snapshot>,
): Snapshot {
  return {
    startedUtc: fields.finishedUtc - 380,
    sourcePaths: [],
    status: "complete",
    ...fields,
  };
}

/**
 * A data source backed by fixed synthetic data.
 *
 * `nowUtc` is injectable so tests are not at the mercy of the wall clock —
 * health is a function of elapsed time, and a suite that drifts into failure
 * overnight is worse than no suite.
 */
export function createDemoApi(
  nowUtc: number = Math.floor(Date.now() / 1000),
): PortalApi {
  // Removals held in closure rather than mutating `buildOverview`'s result:
  // the fixed dataset is rebuilt fresh on every `overview()` call, so a
  // machine "removed" on one call has to be reapplied on the next, exactly
  // like a real removal outliving the request that made it.
  const removedMachines = new Set<string>();

  // Keyed by `machineId/jobId` rather than job id alone: the fixture reuses
  // "photos" across two machines on purpose (the case a naive lookup gets
  // wrong), and a real plan id would never collide the way a demo job id
  // does.
  const clearedJobs = new Set<string>();

  // Real state, not spies: `/demo` has no server behind it, so this is what
  // makes its `/notifications` and per-plan alert cards actually save and
  // reload rather than silently discarding every edit a visitor makes.
  let notificationSettings: NotificationSettings =
    DEFAULT_NOTIFICATION_SETTINGS;
  const planOverrides = new Map<string, PlanNotificationOverride>();

  return {
    isDemo: true,

    overview: () =>
      Promise.resolve(
        withoutRemoved(buildOverview(nowUtc), removedMachines, clearedJobs),
      ),

    removeMachine: (machineId) => {
      removedMachines.add(machineId);
      return Promise.resolve();
    },

    clearJobHistory: (machineId, jobId) => {
      clearedJobs.add(jobKey(machineId, jobId));
      return Promise.resolve();
    },

    notificationSettings: () => Promise.resolve(notificationSettings),

    saveNotificationSettings: (settings) => {
      const normalized = normalizeNotificationSettings(settings);
      if (!normalized.ok)
        return Promise.reject(new PortalError(normalized.message));
      notificationSettings = normalized.settings;
      return Promise.resolve(notificationSettings);
    },

    planNotificationOverride: (planId) =>
      Promise.resolve(planOverrides.get(planId) ?? null),

    savePlanNotificationOverride: (planId, override) => {
      const normalized = normalizePlanOverride(override);
      // Mirrors the real API: an override with nothing left to say is not
      // stored at all, so a later `planNotificationOverride` reports "inherits
      // everything" the same way a plan that never had one does.
      if (overrideInheritsEverything(normalized)) {
        planOverrides.delete(planId);
      } else {
        planOverrides.set(planId, normalized);
      }
      return Promise.resolve(normalized);
    },

    clearPlanNotificationOverride: (planId) => {
      planOverrides.delete(planId);
      return Promise.resolve();
    },

    // Nothing is actually sent — there is no webhook on the other end of a
    // demo — but the URL still gets the same validation a real send would
    // reject it for, so trying a bad URL here behaves like it would for real.
    sendNotificationTest: (_channel, webhookUrl) => {
      const problem = webhookUrlProblem(webhookUrl);
      if (problem) return Promise.reject(new PortalError(problem));
      return Promise.resolve();
    },

    // An imitation, not the real renderer: that one lives in the worker, and
    // pulling it into the browser bundle to make a demo page honest about its
    // own font choices would be the tail wagging the dog. It has the sections
    // and the numbers of the real thing, drawn from the same synthetic
    // overview the dashboard above it is showing.
    summaryPreview: (cadence) =>
      Promise.resolve(
        demoPreview(
          withoutRemoved(buildOverview(nowUtc), removedMachines, clearedJobs),
          cadence,
          nowUtc,
        ),
      ),

    sendSummaryTest: (channel) => {
      const saved = notificationSettings.channels[channel];
      if (channel !== "email" && !saved.webhookUrl) {
        return Promise.reject(
          new PortalError(
            "Save a URL for this channel first, then send yourself a summary.",
          ),
        );
      }
      return Promise.resolve();
    },
  };
}

function demoPreview(
  overview: PortalOverview,
  cadence: Exclude<SummaryCadence, "off">,
  nowUtc: number,
): { subject: string; text: string; html: string } {
  const assessed = overview.jobs.map((job) => ({
    job,
    assessment: assessJob(job, overview.snapshots, nowUtc),
  }));
  const attention = assessed.filter(({ assessment }) =>
    needsAttention(assessment.health),
  );
  const protectedBytes = overview.repositories.reduce(
    (total, repository) => total + (repository.logicalBytes ?? 0),
    0,
  );
  const headline =
    attention.length === 0
      ? "everything is current"
      : `${attention.length} plan${attention.length === 1 ? "" : "s"} need attention`;

  const lines = [
    `${overview.machines.length} machines · ${overview.jobs.length} plans · ${humanBytes(protectedBytes)} protected`,
    ...attention.map(
      ({ job, assessment }) => `• ${job.name} — ${assessment.reason}`,
    ),
    "",
    "https://bigbearbackup.com/dashboard",
  ];

  return {
    subject: `Big Bear Backup: ${summaryCadenceLabel(cadence)} summary — ${headline}`,
    text: lines.join("\n"),
    html: `<html><body style="font-family: system-ui, sans-serif; padding: 24px;">${lines
      .map((line) => `<p>${line}</p>`)
      .join("")}</body></html>`,
  };
}

function jobKey(machineId: string, jobId: string): string {
  return `${machineId}/${jobId}`;
}

/**
 * `overview`, with any machine `removed` names — and everything of theirs —
 * left out, and any `cleared` job's runs emptied without touching the job or
 * machine it belongs to.
 */
function withoutRemoved(
  overview: PortalOverview,
  removed: Set<string>,
  cleared: Set<string>,
): PortalOverview {
  if (removed.size === 0 && cleared.size === 0) return overview;

  return {
    repositories: overview.repositories,
    machines: overview.machines.filter((machine) => !removed.has(machine.id)),
    jobs: overview.jobs.filter((job) => !removed.has(job.machineId)),
    snapshots: overview.snapshots.filter(
      (snapshot) =>
        !removed.has(snapshot.machineId) &&
        !cleared.has(jobKey(snapshot.machineId, snapshot.jobId)),
    ),
  };
}
